import { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import api from 'services/api'
import useToast from 'hooks/useToast'
import { SectionHeading } from './parts'
import {
  CONTROL_WORDS,
  fmtControl,
  fmtCredits,
  fmtHrs,
  fmtUsd,
  fmtWhen,
  poolUsd,
  sumOf,
} from './vocabulary'

const SHADOW_CAPTION =
  'Shadow: every run is sent, and what enforcing would skip is counted.'

// One control: what it is, the lever, and the shadow evidence. The columns
// auto-fit the room they have: three across the page, one on a phone.
const Row = ({ title, scope, control, onReset, children, evidence }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
      columnGap: 4,
      rowGap: 2,
      py: 2.5,
      borderTop: 1,
      borderColor: 'divider',
    }}
  >
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        {scope && <Chip label={scope} size="small" variant="outlined" />}
        {control?.overridden && (
          <Chip label="Overridden" size="small" color="warning" />
        )}
      </Box>
      {children[0]}
      {control && (
        <Typography variant="caption" color="text.secondary" component="div">
          Terraform default: {fmtControl(control.name, control.default)}
          {(control.overridden || control.invalid) && (
            <Button size="small" sx={{ ml: 1 }} onClick={onReset}>
              Reset
            </Button>
          )}
        </Typography>
      )}
    </Box>
    <Box>{children[1]}</Box>
    <Box>{evidence}</Box>
  </Box>
)

const Evidence = ({ label, value, note }) => (
  <>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={700}>
      {value}
    </Typography>
    {note && (
      <Typography variant="caption" color="text.secondary" component="div">
        {note}
      </Typography>
    )}
  </>
)

const ModeToggle = ({ label, value, onChange }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value}
    aria-label={label}
    onChange={(event, next) => next && next !== value && onChange(next)}
  >
    <ToggleButton value="shadow" sx={{ px: 2.5 }}>
      Shadow
    </ToggleButton>
    <ToggleButton value="enforcing" sx={{ px: 2.5 }}>
      Enforcing
    </ToggleButton>
  </ToggleButtonGroup>
)

// The Cost page's spend gates. Every change goes through one confirmation that
// says what it does and asks why: the server refuses a save without a reason,
// and the change log below shows it. The page's "Spend controls" button
// scrolls here.
const SpendControls = ({ view, cost, onSaved }) => {
  const toast = useToast()
  const [dialog, setDialog] = useState(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const controls = view?.controls || {}
  // `invalid`: a stored override the API no longer accepts, which a reset clears.
  const control = (name) =>
    controls[name]
      ? { name, ...controls[name], invalid: Boolean(view?.invalid?.[name]) }
      : null
  const ceiling = controls.daily_ceiling_hours?.value ?? null
  const limit = controls.unreviewed_limit?.value ?? null
  const [ceilingDraft, setCeilingDraft] = useState('')
  const [limitDraft, setLimitDraft] = useState('')
  useEffect(
    () => setCeilingDraft(ceiling == null ? '' : String(ceiling)),
    [ceiling]
  )
  useEffect(() => setLimitDraft(limit == null ? '' : String(limit)), [limit])

  const daily = cost?.daily || []
  const windowDays = cost?.days
  // The contract's prices off the cost read, for the ceiling's and the
  // overage's dollars; an API that sends none leaves them unsaid.
  const pool = cost?.cycle
  const hourUsd = poolUsd(pool?.credits_per_audio_hour, pool)
  const overageRate = pool?.overage_credit_usd
  const skipped = (gate) => sumOf(daily, (row) => row.would_skip_hours?.[gate])
  const today = daily.find(({ books }) => books === 'running')
  const poolShare = today?.allowance_hours ?? null
  const allowed =
    poolShare != null && ceiling != null
      ? Math.min(poolShare, ceiling)
      : poolShare
  const selection = sumOf(
    daily,
    (row) => (row.usd.find_moments || 0) + (row.usd.judge || 0)
  )

  const ask = (texts, changes, reset = []) => {
    setReason('')
    setDialog({ ...texts, changes, reset })
  }
  const flip = (name, to) => {
    const enforce = to === 'enforcing'
    const texts = {
      allowance_mode: enforce
        ? {
            title: 'Enforce the daily allowance?',
            body: "From the next transcription pass, a run past today's allowance is skipped. A skipped run is recorded and never transcribed later, so no quote can come from it.",
            evidence: `In the last ${windowDays} days enforcing would have skipped ${fmtHrs(
              skipped('allowance')
            )}. Today ${fmtHrs(today?.transcribed_hours)} of ${fmtHrs(
              allowed
            )} is used.`,
            caution:
              "The pool is shared with manual clips and the other environments. If the account has already spent its included credits this cycle, today's allowance is 0 and enforcing stops stream transcription until the reset.",
            confirm: 'Enforce',
          }
        : {
            title: 'Return the daily allowance to shadow?',
            body: 'Every run is sent again, past the allowance and the ceiling too. What enforcing would have skipped is still counted.',
            caution:
              'Spending past the pool then shows only as a Sentry alert from the reconciliation.',
            confirm: 'Return to shadow',
          },
      credit_mode: enforce
        ? {
            title: 'Enforce the per-user credit bucket?',
            body: 'An account that has used its 8 h is skipped until its next refill of 1 h every 3 h. A skipped run is never transcribed later.',
            evidence: `In the last ${windowDays} days enforcing would have skipped ${fmtHrs(
              skipped('credits')
            )}.`,
            caution:
              'An ordinary day stays inside 8 h. What this stops is a television or a podcast playing all day.',
            confirm: 'Enforce',
          }
        : {
            title: 'Return the credit bucket to shadow?',
            body: "Every run is sent again, whatever the account's balance. What enforcing would have skipped is still counted.",
            confirm: 'Return to shadow',
          },
      unreviewed_mode: enforce
        ? {
            title: 'Enforce the unreviewed-quotes gate?',
            body: `From each account's next pass, an account with ${limit} or more quotes waiting for review has its new audio skipped until it reviews. A skipped run is never transcribed later.`,
            evidence: `In the last ${windowDays} days enforcing would have skipped ${fmtHrs(
              skipped('unreviewed')
            )}.`,
            caution: `Phones on the legacy app have no review tray, so their quotes always wait: enforcing stops their transcription once ${limit} are waiting.`,
            confirm: 'Enforce',
          }
        : {
            title: 'Return the unreviewed-quotes gate to shadow?',
            body: 'Every run is sent again, whatever is waiting for review. What enforcing would have skipped is still counted.',
            confirm: 'Return to shadow',
          },
    }
    ask(texts[name], { [name]: to })
  }
  const saveCeiling = () => {
    const raw = ceilingDraft.trim()
    const hours = raw ? Number(raw) : null
    if (raw && !(hours > 0)) {
      toast({
        message:
          'The ceiling is a positive number of hours, or empty for none.',
        severity: 'error',
      })
      return
    }
    ask(
      hours == null
        ? {
            title: 'Remove the daily ceiling?',
            body: `Today's allowance goes back to the pool share alone${
              poolShare != null ? `, ${fmtHrs(poolShare)}` : ''
            }, from the next run.`,
            confirm: 'Remove',
          }
        : {
            title: `Set a daily ceiling of ${fmtHrs(hours)}?`,
            body: `From the next run, today's allowance is the smaller of the pool share${
              poolShare != null ? ` (${fmtHrs(poolShare)})` : ''
            } and ${fmtHrs(hours)}, in the daily allowance's mode: ${
              controls.allowance_mode?.value === 'enforcing'
                ? 'enforcing, so runs past it are skipped.'
                : 'shadow, so runs past it are counted and still sent.'
            }`,
            evidence:
              hourUsd == null
                ? null
                : `About ${fmtUsd(
                    hours * hourUsd
                  )} a day at the contract rate.`,
            confirm: 'Set the ceiling',
          },
      { daily_ceiling_hours: hours }
    )
  }
  const saveLimit = () => {
    const quotes = Number(limitDraft)
    if (!Number.isInteger(quotes) || quotes < 1) {
      toast({
        message: 'The limit is a whole number of quotes.',
        severity: 'error',
      })
      return
    }
    ask(
      {
        title: `Stop transcribing at ${quotes} quotes waiting?`,
        body: `From each account's next pass, the unreviewed-quotes gate counts a tray as full at ${quotes} quotes waiting for review, in the gate's own mode (${controls.unreviewed_mode?.value}).`,
        confirm: 'Set the limit',
      },
      { unreviewed_limit: quotes }
    )
  }
  const reset = (name) =>
    ask(
      {
        title: `Give the ${CONTROL_WORDS[
          name
        ].toLowerCase()} back to Terraform?`,
        body: `It goes back to ${fmtControl(
          name,
          controls[name]?.default
        )}, the value Terraform sets for this environment, from the next pass.`,
        confirm: 'Reset',
      },
      {},
      [name]
    )

  const confirm = async () => {
    setSaving(true)
    try {
      const next = await api.streamControls.saveControls({
        changes: dialog.changes,
        reset: dialog.reset,
        reason: reason.trim(),
      })
      onSaved(next)
      setDialog(null)
      toast({
        message: 'Saved. It applies from the next pass.',
        severity: 'success',
      })
    } catch (err) {
      toast({ message: err.message, severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const history = view?.history || []
  const modeCaption = (name, enforced) =>
    controls[name]?.value === 'enforcing' ? enforced : SHADOW_CAPTION

  return (
    <>
      <SectionHeading
        title="Spend controls"
        help="Each control starts at Terraform's default for this environment. A change overrides it from the next transcription pass, needs a reason, and is kept in the change log below."
      />
      {view?.invalid && Object.keys(view.invalid).length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          A stored override is no longer accepted and Terraform's value is in
          force:{' '}
          {Object.entries(view.invalid)
            .map(([name, why]) => `${CONTROL_WORDS[name] || name} (${why})`)
            .join(', ')}
        </Alert>
      )}

      <Row
        title="Daily allowance"
        scope="Whole environment"
        control={control('allowance_mode')}
        onReset={() => reset('allowance_mode')}
        evidence={
          <Evidence
            label="Enforcing would have skipped"
            value={fmtHrs(skipped('allowance'))}
            note={`in the last ${windowDays || '—'} days · ${
              allowed == null
                ? 'today not fixed yet: no run so far'
                : `today ${fmtHrs(today?.transcribed_hours)} of ${fmtHrs(
                    allowed
                  )}`
            }`}
          />
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          This environment's share of the ElevenLabs pool for the day: the
          credits left this cycle divided by the days to reset, fixed at the
          day's first run.
        </Typography>
        <>
          <ModeToggle
            label="Daily allowance mode"
            value={controls.allowance_mode?.value}
            onChange={(to) => flip('allowance_mode', to)}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {modeCaption(
              'allowance_mode',
              "Enforcing: a run past today's allowance is skipped for good."
            )}
          </Typography>
        </>
      </Row>

      <Row
        title="Daily ceiling"
        control={control('daily_ceiling_hours')}
        onReset={() => reset('daily_ceiling_hours')}
        evidence={
          <Evidence
            label="Today's allowance"
            value={fmtHrs(allowed)}
            note={
              allowed == null
                ? "fixed at the day's first run; none so far today"
                : ceiling != null && poolShare != null && ceiling < poolShare
                ? `set by the ceiling; the pool share is ${fmtHrs(poolShare)}`
                : 'the pool share'
            }
          />
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          An upper limit on this environment's transcription per day, under the
          pool share, in the daily allowance's mode. Empty means the pool share
          alone.
        </Typography>
        <>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Hours per day"
              type="number"
              size="small"
              placeholder="None"
              value={ceilingDraft}
              onChange={(event) => setCeilingDraft(event.target.value)}
              inputProps={{ min: 1, step: 10 }}
              sx={{ width: 150 }}
            />
            <Button
              variant="contained"
              size="small"
              disableElevation
              sx={{ mt: 0.5 }}
              disabled={
                ceilingDraft.trim() === (ceiling == null ? '' : String(ceiling))
              }
              onClick={saveCeiling}
            >
              Save
            </Button>
          </Box>
          {hourUsd != null && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Each hour is about {fmtUsd(hourUsd)} at the contract rate.
            </Typography>
          )}
        </>
      </Row>

      <Row
        title="Per-user credit bucket"
        scope="Each account"
        control={control('credit_mode')}
        onReset={() => reset('credit_mode')}
        evidence={
          <Evidence
            label="Enforcing would have skipped"
            value={fmtHrs(skipped('credits'))}
            note={`in the last ${windowDays || '—'} days`}
          />
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Each account holds up to 8 h of transcription and gets 1 h back every
          3 h, so a television or a podcast left playing cannot take the pool.
        </Typography>
        <>
          <ModeToggle
            label="Credit bucket mode"
            value={controls.credit_mode?.value}
            onChange={(to) => flip('credit_mode', to)}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {modeCaption(
              'credit_mode',
              'Enforcing: an account out of credit is skipped until its next refill.'
            )}
          </Typography>
        </>
      </Row>

      <Row
        title="Unreviewed quotes"
        scope="Each account"
        control={control('unreviewed_mode')}
        onReset={() => reset('unreviewed_mode')}
        evidence={
          <Evidence
            label="Enforcing would have skipped"
            value={fmtHrs(skipped('unreviewed'))}
            note={`in the last ${windowDays || '—'} days`}
          />
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Stop transcribing an account's new audio while this many of its quotes
          wait for review. Reviewing starts it again; audio skipped meanwhile is
          not transcribed later.
        </Typography>
        <>
          <ModeToggle
            label="Unreviewed quotes mode"
            value={controls.unreviewed_mode?.value}
            onChange={(to) => flip('unreviewed_mode', to)}
          />
          <Box
            sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 2 }}
          >
            <TextField
              label="Quotes waiting"
              type="number"
              size="small"
              value={limitDraft}
              onChange={(event) => setLimitDraft(event.target.value)}
              inputProps={{ min: 1, step: 1 }}
              sx={{ width: 130 }}
            />
            <Button
              variant="contained"
              size="small"
              disableElevation
              sx={{ mt: 0.5 }}
              disabled={limitDraft.trim() === String(limit)}
              onClick={saveLimit}
            >
              Save
            </Button>
            {(controls.unreviewed_limit?.overridden ||
              control('unreviewed_limit')?.invalid) && (
              <Button
                size="small"
                sx={{ mt: 0.5 }}
                onClick={() => reset('unreviewed_limit')}
              >
                Reset
              </Button>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {modeCaption(
              'unreviewed_mode',
              `Enforcing: an account with ${limit} quotes waiting is skipped until it reviews.`
            )}
          </Typography>
        </>
      </Row>

      <Row
        title="Overage past the pool"
        scope="Set at deploy"
        evidence={
          <Evidence
            label="Account overage now"
            value={fmtUsd(Number(cost?.cycle?.current_overage) || 0)}
            note={`Extension cap: ${
              pool?.max_credit_limit_extension == null
                ? 'not reported'
                : `${fmtCredits(pool.max_credit_limit_extension)} credits${
                    overageRate == null
                      ? ''
                      : `, ${fmtUsd(
                          pool.max_credit_limit_extension * overageRate
                        )} at the overage rate`
                  }`
            }`}
          />
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          ElevenLabs bills credits past the included pool
          {overageRate != null &&
            ` at $${overageRate} each, about ${fmtUsd(
              overageRate * pool.credits_per_audio_hour
            )} an audio hour`}
          .
        </Typography>
        <Typography variant="body2">
          None allowed: the stream lane never spends past the pool.
        </Typography>
      </Row>

      <Row
        title="Quote selection · Gemini 2.5 Flash"
        scope="Set at deploy"
        evidence={
          <Evidence
            label={`Last ${windowDays || '—'} days`}
            value={fmtUsd(selection)}
            note="find moments and judge together"
          />
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Paid per token, with no spend gate of its own: every transcript goes
          to the selector and then the judge.
        </Typography>
        <Box
          component="dl"
          sx={{
            m: 0,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            columnGap: 2,
            rowGap: 0.5,
            '& dt': { color: 'text.secondary' },
            '& dd': { m: 0 },
            typography: 'body2',
          }}
        >
          <dt>Selector thinking</dt>
          <dd>Model default</dd>
          <dt>Judge</dt>
          <dd>7 duels per moment, no thinking</dd>
          <dt>Moments per span</dt>
          <dd>No cap</dd>
        </Box>
      </Row>

      <Box sx={{ pt: 2.5, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Changes
        </Typography>
        {history.length ? (
          <TableContainer sx={{ maxHeight: 320 }}>
            {/* On a phone a row keeps to one line, but for the reason, which
                wraps in a column of its own, and the box scrolls sideways. */}
            <Table
              size="small"
              stickyHeader
              sx={(theme) => ({
                [theme.breakpoints.down('desktop')]: {
                  '& td:not(:last-of-type)': { whiteSpace: 'nowrap' },
                },
              })}
            >
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Who</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Why</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((entry) => (
                  <TableRow key={`${entry.at}-${entry.by_user_id}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {fmtWhen(entry.at)}
                    </TableCell>
                    <TableCell>{entry.by}</TableCell>
                    <TableCell>
                      {entry.changes.map(
                        ({ control: name, before, after, overridden }) => (
                          <div key={name}>
                            {CONTROL_WORDS[name] || name}:{' '}
                            {fmtControl(name, before)} →{' '}
                            {fmtControl(name, after)}
                            {overridden ? '' : ' (Terraform)'}
                          </div>
                        )
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'text.secondary',
                        minWidth: { mobile: 240, desktop: 0 },
                      }}
                    >
                      {entry.reason}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No changes yet. Each change is recorded with who made it, when, and
            why.
          </Typography>
        )}
      </Box>

      {/* An explicit width: `maxWidth="sm"` reads a breakpoint this theme
          does not define (it has mobile and desktop), so the dialog would
          stretch across the page. */}
      <Dialog
        open={Boolean(dialog)}
        onClose={() => !saving && setDialog(null)}
        maxWidth={false}
        PaperProps={{ sx: { width: '100%', maxWidth: 520 } }}
      >
        {dialog && (
          <>
            <DialogTitle>{dialog.title}</DialogTitle>
            <DialogContent
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <Typography variant="body2">{dialog.body}</Typography>
              {dialog.evidence && (
                <Alert severity="info">{dialog.evidence}</Alert>
              )}
              {dialog.caution && (
                <Alert severity="warning">{dialog.caution}</Alert>
              )}
              <TextField
                label="Why (kept in the change log)"
                multiline
                minRows={2}
                InputLabelProps={{ required: true }}
                autoFocus
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialog(null)} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={confirm}
                disabled={saving || !reason.trim()}
                startIcon={saving ? <CircularProgress size={14} /> : undefined}
              >
                {dialog.confirm}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}

export default SpendControls
